import { Alert, AlertIcon, HStack, Text, VStack } from "@chakra-ui/react";
import PatientProfileEditModeButton from "../../../../entityProfileModal/components/EntityProfileEditModeButton";
import PatientProfileField from "../../../../entityProfileModal/components/EntityProfileField";
import type { PatientProfileBridgeActions } from "../../../patientProfileModal.actions";
import type { PatientProfilePatient } from "../../../patientProfileModal.types";
import {
  PatientContactEditorCard,
  ReadOnlyPatientContactCard,
} from "./components/PatientContactCards";
import { usePatientProfilePatientContactsController } from "./PatientProfilePatientContactsSection.hooks";
import EntityModalButton from "../../../../entityProfileModal/components/EntityModalButton";

type Props = {
  actions: PatientProfileBridgeActions;
  patient: PatientProfilePatient;
};

export default function PatientProfilePatientContactsSection(props: Props) {
  const controller = usePatientProfilePatientContactsController(props.patient);
  const patientMainPhoneNumber =
    (props.patient as { mainPhoneNumber?: string | null }).mainPhoneNumber ?? null;

  if (controller.isEditing) {
    return (
      <VStack alignItems="stretch" spacing={4}>
        <HStack justifyContent="flex-end">
          <EntityModalButton
            isIconOnly
            aria-label="Save contacts"
            iconClassName="fa fa-check"
            isLoading={controller.isSaving}
            kind="blue"
            size="sm"
            title="Save contacts"
            onClick={controller.saveContacts}
          />
          <PatientProfileEditModeButton
            isDisabled={controller.isSaving}
            isEditing={true}
            label="Edit"
            onClick={controller.cancelEditing}
          />
        </HStack>
        {controller.validationMessage ? (
          <Alert status="error">
            <AlertIcon />
            <Text>{controller.validationMessage}</Text>
          </Alert>
        ) : null}
        {controller.draftContacts.map((contact, index) => (
          <PatientContactEditorCard
            key={contact.id ?? `new-${index}`}
            addressTimezone={props.patient.address?.timezone ?? "America/New_York"}
            contact={contact}
            index={index}
            isDisabled={controller.isSaving}
            isOnboardingAgency={controller.isOnboardingAgency}
            mainPhoneNumber={patientMainPhoneNumber}
            showErrors={controller.showErrors}
            onChange={controller.updateDraftContact}
            onRemove={controller.removeDraftContact}
            onUpdateMainPhoneNumber={controller.updateMainPhoneNumber}
          />
        ))}
        <EntityModalButton
          disabled={controller.isSaving}
          iconClassName="fa fa-plus"
          kind="white"
          size="sm"
          style={{ alignSelf: "flex-start" }}
          onClick={controller.addDraftContact}
        >
          {controller.draftContacts.length > 0 ? "Add another contact" : "Add contact"}
        </EntityModalButton>
      </VStack>
    );
  }

  if (controller.contacts.length === 0) {
    return (
      <VStack alignItems="stretch" spacing={4}>
        {controller.canEdit ? (
          <PatientProfileEditModeButton
            isEditing={false}
            label="Edit"
            onClick={controller.startEditing}
          />
        ) : null}
        <PatientProfileField label="Contacts" value="" />
      </VStack>
    );
  }

  return (
    <VStack alignItems="stretch" spacing={4}>
      {controller.canEdit ? (
        <PatientProfileEditModeButton
          isEditing={false}
          label="Edit"
          onClick={controller.startEditing}
        />
      ) : null}
      {controller.contacts.map((contact, index) => (
        <ReadOnlyPatientContactCard
          key={contact.id}
          actions={props.actions}
          contact={contact}
          index={index}
          isOnboardingAgency={controller.isOnboardingAgency}
          mainPhoneNumber={patientMainPhoneNumber}
          patientId={props.patient.id}
        />
      ))}
    </VStack>
  );
}
